/**
 * Событие Chrome «страницу можно установить». В типах TypeScript его нет —
 * спецификация не стандартизована, но именно оно даёт единственный способ
 * показать установку по кнопке, а не ждать инициативы браузера.
 */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | null = null;

/**
 * Сохраняет событие установки. Показать окно можно только из жеста человека и
 * только один раз на событие, поэтому храним его до нажатия кнопки.
 */
export function keepInstallPrompt(event: BeforeInstallPromptEvent): void {
  deferred = event;
}

export function hasInstallPrompt(): boolean {
  return deferred !== null;
}

/**
 * Показывает окно установки. Возвращает `true`, если человек согласился.
 * Событие одноразовое: после показа сбрасываем его в любом случае.
 */
export async function showInstallPrompt(): Promise<boolean> {
  if (!deferred) return false;

  const event = deferred;
  deferred = null;

  try {
    await event.prompt();
    const choice = await event.userChoice;
    return choice.outcome === "accepted";
  } catch (error) {
    console.error("PWA: окно установки не открылось", error);
    return false;
  }
}

export function forgetInstallPrompt(): void {
  deferred = null;
}

/** Кабинет открыт как установленное приложение, а не вкладкой браузера. */
export function isRunningInstalled(): boolean {
  if (typeof window === "undefined") return false;

  // iOS до сих пор не поддерживает display-mode: standalone и сообщает об
  // установленном приложении собственным нестандартным полем навигатора.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  const displayMode =
    typeof window.matchMedia === "function" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: minimal-ui)").matches);

  return iosStandalone || displayMode;
}
